import React, { useState, useEffect } from 'react';
import AppLayout from '../components/layout/AppLayout';
import {
  getProjects, getSubProjects, createSubProject,
  updateSubProject, deleteSubProject,
} from '../services/api';
import Swal from 'sweetalert2';
import Select from '../components/Select';
import { FolderOpen, FolderGit2, Plus, Pencil, Trash2, CheckCircle, Clock, PauseCircle, ChevronRight } from 'lucide-react';

const STATUS_META = {
  active:    { label: 'Active',     color: '#059669', bg: '#d1fae5', Icon: CheckCircle },
  on_hold:   { label: 'On Hold',    color: '#d97706', bg: '#fef3c7', Icon: PauseCircle },
  completed: { label: 'Completed',  color: '#6b7280', bg: '#f3f4f6', Icon: Clock       },
};

const EMPTY_FORM = { name: '', description: '', status: 'active', start_date: '', end_date: '' };

export default function SubProjectsPage() {
  const [projects,    setProjects]    = useState([]);
  const [subProjects, setSubProjects] = useState([]);
  const [selProject,  setSelProject]  = useState(null); // selected project object
  const [loading,     setLoading]     = useState(true);
  const [subLoading,  setSubLoading]  = useState(false);

  // Modal state
  const [modal, setModal]   = useState(null); // null | 'create' | 'edit'
  const [editing, setEditing] = useState(null); // sub-project being edited
  const [form, setForm]     = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Load all projects once
  useEffect(() => {
    getProjects()
      .then(r => {
        setProjects(r.data || []);
        if (r.data?.length) setSelProject(r.data[0]);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Load sub-projects when selected project changes
  useEffect(() => {
    if (!selProject) return;
    setSubLoading(true);
    getSubProjects({ project_id: selProject.id })
      .then(r => setSubProjects(r.data || []))
      .catch(() => {})
      .finally(() => setSubLoading(false));
  }, [selProject]);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setEditing(null);
    setModal('create');
  };

  const openEdit = (sp) => {
    setForm({
      name:        sp.name        || '',
      description: sp.description || '',
      status:      sp.status      || 'active',
      start_date:  sp.start_date  ? sp.start_date.slice(0, 10) : '',
      end_date:    sp.end_date    ? sp.end_date.slice(0, 10)   : '',
    });
    setEditing(sp);
    setModal('edit');
  };

  const closeModal = () => { setModal(null); setEditing(null); setForm(EMPTY_FORM); };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (modal === 'create') {
        await createSubProject({ ...form, project_id: selProject.id });
      } else {
        await updateSubProject(editing.id, form);
      }
      closeModal();
      // Refresh sub-projects
      const r = await getSubProjects({ project_id: selProject.id });
      setSubProjects(r.data || []);
    } catch (err) {
      Swal.fire('Error', err.response?.data?.error || 'Could not save project.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (sp) => {
    const res = await Swal.fire({
      title: 'Delete Project?',
      html: `<b>${sp.name}</b> will be removed.<br/>Linked tickets will be unlinked (not deleted).`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: '#dc2626',
    });
    if (!res.isConfirmed) return;
    try {
      await deleteSubProject(sp.id);
      setSubProjects(prev => prev.filter(x => x.id !== sp.id));
    } catch {
      Swal.fire('Error', 'Could not delete project.', 'error');
    }
  };

  return (
    <AppLayout pageTitle="Projects">
      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 20, height: 'calc(100vh - 104px)', minHeight: 0 }}>

        {/* ── LEFT: Project List ── */}
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <FolderOpen size={16} color="var(--primary)" />
            <span style={{ fontWeight: 700, fontSize: 13.5, color: 'var(--text-main)' }}>Clients</span>
            <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>{projects.length}</span>
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loading ? (
              <div style={{ padding: 24, textAlign: 'center' }}><div className="spinner" style={{ width: 24, height: 24, margin: '0 auto' }} /></div>
            ) : projects.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No clients yet.</div>
            ) : (
              projects.map(p => {
                const isActive = selProject?.id === p.id;
                return (
                  <div
                    key={p.id}
                    onClick={() => setSelProject(p)}
                    style={{
                      padding: '11px 16px',
                      cursor: 'pointer',
                      borderLeft: `3px solid ${isActive ? 'var(--primary)' : 'transparent'}`,
                      background: isActive ? 'var(--primary-light)' : 'transparent',
                      display: 'flex', alignItems: 'center', gap: 10,
                      transition: 'all 0.12s',
                    }}
                    onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'var(--bg-hover)'; }}
                    onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <FolderOpen size={15} color={isActive ? 'var(--primary)' : 'var(--text-muted)'} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: isActive ? 700 : 500, color: isActive ? 'var(--primary)' : 'var(--text-main)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.name}
                      </div>
                      <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 1 }}>
                        {p.project_type === 'sap' ? '🔷 SAP' : '🟢 Non-SAP'}
                      </div>
                    </div>
                    <ChevronRight size={13} color={isActive ? 'var(--primary)' : 'var(--text-muted)'} />
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* ── RIGHT: Sub-Projects ── */}
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <FolderGit2 size={18} color="var(--primary)" />
              <div>
                <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--text-main)' }}>
                  {selProject ? selProject.name : 'Select a Client'}
                </h2>
                {selProject && (
                  <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)' }}>
                    {subProjects.length} project{subProjects.length !== 1 ? 's' : ''}
                  </p>
                )}
              </div>
            </div>
            {selProject && (
              <button
                onClick={openCreate}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, border: 'none', background: 'var(--primary)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
              >
                <Plus size={14} /> New Project
              </button>
            )}
          </div>

          {/* Sub-project cards */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {!selProject ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 260, color: 'var(--text-muted)', flexDirection: 'column', gap: 10 }}>
                <FolderOpen size={36} style={{ opacity: 0.3 }} />
                <span style={{ fontSize: 14 }}>Select a client from the left to manage its projects.</span>
              </div>
            ) : subLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200 }}>
                <div className="spinner" style={{ width: 28, height: 28 }} />
              </div>
            ) : subProjects.length === 0 ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 260, flexDirection: 'column', gap: 12, background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)' }}>
                <FolderGit2 size={36} style={{ opacity: 0.3 }} color="var(--text-muted)" />
                <p style={{ margin: 0, fontSize: 14, color: 'var(--text-muted)' }}>No projects yet for <strong>{selProject.name}</strong>.</p>
                <button onClick={openCreate} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 18px', borderRadius: 8, border: 'none', background: 'var(--primary)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  <Plus size={14} /> Create First Project
                </button>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
                {subProjects.map(sp => {
                  const s = STATUS_META[sp.status] || STATUS_META.active;
                  return (
                    <div key={sp.id} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {/* Top row */}
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-main)', lineHeight: 1.3 }}>{sp.name}</div>
                          {sp.description && (
                            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.5 }}>{sp.description}</div>
                          )}
                        </div>
                        <span style={{ fontSize: 10.5, fontWeight: 700, padding: '3px 9px', borderRadius: 20, background: s.bg, color: s.color, whiteSpace: 'nowrap', flexShrink: 0 }}>
                          {s.label}
                        </span>
                      </div>

                      {/* Dates */}
                      {(sp.start_date || sp.end_date) && (
                        <div style={{ display: 'flex', gap: 14, fontSize: 12, color: 'var(--text-muted)' }}>
                          {sp.start_date && <span>📅 Start: {sp.start_date.slice(0,10)}</span>}
                          {sp.end_date   && <span>🏁 End: {sp.end_date.slice(0,10)}</span>}
                        </div>
                      )}

                      {/* Stats */}
                      <div style={{ display: 'flex', gap: 16, paddingTop: 8, borderTop: '1px solid var(--border-light)', fontSize: 12, color: 'var(--text-muted)' }}>
                        <span>🎫 {sp.open_tickets || 0} open</span>
                        <span>📦 {sp.total_tickets || 0} total</span>
                        {sp.created_by_name && <span style={{ marginLeft: 'auto' }}>👤 {sp.created_by_name}</span>}
                      </div>

                      {/* Actions */}
                      <div style={{ display: 'flex', gap: 8, paddingTop: 2 }}>
                        <button
                          onClick={() => openEdit(sp)}
                          style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '6px 0', borderRadius: 7, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-sub)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}
                          onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-hover)'; e.currentTarget.style.color = 'var(--primary)'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-sub)'; }}
                        >
                          <Pencil size={12} /> Edit
                        </button>
                        <button
                          onClick={() => handleDelete(sp)}
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '6px 14px', borderRadius: 7, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-muted)', fontSize: 12.5, cursor: 'pointer' }}
                          onMouseEnter={e => { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.color = '#dc2626'; e.currentTarget.style.borderColor = '#fca5a5'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Modal ── */}
      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}>
          <div className="modal modal-sm">
            <div className="modal-header">
              <span className="modal-title">{modal === 'create' ? 'New Project' : 'Edit Project'}</span>
              <button className="btn btn-ghost btn-icon btn-sm" onClick={closeModal}>✕</button>
            </div>
            <form onSubmit={handleSave}>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {/* Project (readonly on create) */}
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Client</label>
                  <input className="form-control" value={selProject?.name || ''} readOnly style={{ opacity: 0.7 }} />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Project Name <span className="required">*</span></label>
                  <input
                    className="form-control"
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Finance Module Rollout, Phase 2 – HR"
                    required
                    autoFocus
                  />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={form.description}
                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="Brief description of this phase or module stream..."
                  />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Status</label>
                  <Select
                    value={form.status}
                    onChange={v => setForm(f => ({ ...f, status: v }))}
                    options={[
                      { value: 'active', label: 'Active' },
                      { value: 'on_hold', label: 'On Hold' },
                      { value: 'completed', label: 'Completed' },
                    ]}
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Start Date</label>
                    <input type="date" className="form-control" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">End Date</label>
                    <input type="date" className="form-control" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving || !form.name.trim()}>
                  {saving ? 'Saving…' : modal === 'create' ? 'Create Project' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
